import type { PickEntry, ManagerPickEntry, CompareMode } from '../types'
import { derivePointsFromScore } from '../types'
import type { ScoringRules } from '@/hooks/useLeagueConfig'
import { getShortName, getDisplayName } from '@/lib/teams'

function formatKickoffTime(isoString: string): string {
  const date = new Date(isoString)
  const now = new Date()
  const isToday = date.toDateString() === now.toDateString()
  const tomorrow = new Date(now)
  tomorrow.setDate(tomorrow.getDate() + 1)
  const isTomorrow = date.toDateString() === tomorrow.toDateString()

  const timeStr = date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })

  if (isToday) return `Today ${timeStr}`
  if (isTomorrow) return `Tomorrow ${timeStr}`

  // Show short date for other days
  const dateStr = date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
  return `${dateStr} ${timeStr}`
}

interface PickRowProps {
  pick: PickEntry
  compareMode: CompareMode
  comparisonPick?: ManagerPickEntry
  comparisonLabel?: string
  scoringRules: ScoringRules
}

function TeamLogo({ src, name }: { src: string; name: string }) {
  // If it's an emoji fallback (single char or emoji), render as text
  if (src.length <= 2 || /^\p{Emoji}/u.test(src)) {
    return (
      <span className="inline-flex items-center justify-center w-6 h-6 rounded-full
                        bg-zinc-100 dark:bg-zinc-800 text-xs">
        {src}
      </span>
    )
  }

  return (
    <img
      src={src}
      alt={name}
      className="w-6 h-6 object-contain shrink-0"
    />
  )
}

function ResultBadge({ result }: { result: 'win' | 'draw' | 'loss' | null }) {
  if (!result) {
    return (
      <span className="inline-flex items-center justify-center w-6 h-6 rounded-md
                        text-xs font-bold bg-zinc-100 dark:bg-zinc-800 text-zinc-400 dark:text-zinc-500">
        -
      </span>
    )
  }

  const styles = {
    win: 'bg-emerald-100 dark:bg-emerald-900/40 text-emerald-700 dark:text-emerald-300',
    draw: 'bg-zinc-100 dark:bg-zinc-800 text-zinc-600 dark:text-zinc-400',
    loss: 'bg-red-100 dark:bg-red-900/40 text-red-700 dark:text-red-300',
  }
  const labels = { win: 'W', draw: 'D', loss: 'L' }

  return (
    <span className={`inline-flex items-center justify-center w-6 h-6 rounded-md
                      text-xs font-bold ${styles[result]}`}>
      {labels[result]}
    </span>
  )
}

function PointsBadge({ points }: { points: number }) {
  const isPositive = points > 0
  const isNegative = points < 0

  return (
    <span className={`inline-flex items-center text-sm font-bold tabular-nums
                      ${isPositive ? 'text-emerald-600 dark:text-emerald-400' : ''}
                      ${isNegative ? 'text-red-600 dark:text-red-400' : ''}
                      ${!isPositive && !isNegative ? 'text-zinc-400 dark:text-zinc-500' : ''}`}>
      {isPositive && '+'}{points}
    </span>
  )
}

function InlineBadge({ label, variant }: { label: string; variant: 'tdd' | 'redcard' | 'autopick' | 'forfeit' }) {
  const styles = {
    tdd: 'bg-violet-100 dark:bg-violet-900/40 text-violet-700 dark:text-violet-300 border-violet-200 dark:border-violet-800',
    redcard: 'bg-red-100 dark:bg-red-900/40 text-red-700 dark:text-red-300 border-red-200 dark:border-red-800',
    autopick: 'bg-amber-100 dark:bg-amber-900/40 text-amber-700 dark:text-amber-300 border-amber-200 dark:border-amber-800',
    forfeit: 'bg-orange-100 dark:bg-orange-900/40 text-orange-700 dark:text-orange-300 border-orange-200 dark:border-orange-800',
  }

  return (
    <span className={`inline-flex items-center px-1.5 py-0.5 rounded-md border
                      text-[10px] font-semibold uppercase tracking-wide ${styles[variant]}`}>
      {label}
    </span>
  )
}

function ComparisonColumn({ comparisonPick }: {
  comparisonPick: ManagerPickEntry
}) {
  return (
    <div className="text-right">
      {/* Team and result row */}
      <div className="flex items-center justify-end gap-2 whitespace-nowrap">
        <TeamLogo src={comparisonPick.teamLogo} name={comparisonPick.teamName} />
        <span className="text-sm font-semibold text-zinc-900 dark:text-zinc-100 truncate">
          {/* Show short name on mobile, full name on larger screens */}
          <span className="sm:hidden">{getShortName(comparisonPick.teamName)}</span>
          <span className="hidden sm:inline">{comparisonPick.teamName}</span>
        </span>
        <ResultBadge result={comparisonPick.result} />
        <PointsBadge points={comparisonPick.points} />
      </div>

      {/* Match context row: H/A badge, vs Opponent, Score */}
      {(comparisonPick.opponentName || comparisonPick.isHome !== null) && (
        <div className="flex items-center justify-end gap-1.5 mt-1 whitespace-nowrap overflow-hidden">
          {comparisonPick.isHome !== null && (
            <span className={`inline-flex items-center justify-center w-4 h-4 shrink-0 rounded text-[9px] font-bold
                              ${comparisonPick.isHome
                                ? 'bg-zinc-900 dark:bg-zinc-100 text-white dark:text-zinc-900'
                                : 'bg-zinc-200 dark:bg-zinc-700 text-zinc-600 dark:text-zinc-400'}`}>
              {comparisonPick.isHome ? 'H' : 'A'}
            </span>
          )}
          {comparisonPick.opponentName && (
            <span className="text-xs text-zinc-500 dark:text-zinc-400 truncate">
              {/* Show short opponent name on mobile */}
              <span className="sm:hidden">vs {getShortName(comparisonPick.opponentName)}</span>
              <span className="hidden sm:inline">vs {comparisonPick.opponentName}</span>
            </span>
          )}
          {comparisonPick.score && (
            <span className="text-xs tabular-nums font-medium text-zinc-600 dark:text-zinc-300 shrink-0">
              {comparisonPick.score.picked}&#8211;{comparisonPick.score.opponent}
            </span>
          )}
        </div>
      )}

      {/* DD badge */}
      {comparisonPick.tddUsed && (
        <div className="flex items-center justify-end gap-1.5 mt-1.5">
          <InlineBadge label="DD" variant="tdd" />
        </div>
      )}
    </div>
  )
}

export function PickRow({ pick, compareMode, comparisonPick, scoringRules }: PickRowProps) {
  const isComparing = compareMode !== 'none'
  const hasGdBonus = pick.gdBonus > 0
  const hasRedCard = pick.redCardPenalty < 0

  // Derive result and points from score using league scoring rules (for unprocessed picks)
  const derived = derivePointsFromScore(pick.score, pick.tddUsed, scoringRules)
  const displayResult = pick.result ?? derived.result
  const displayPoints = pick.processed ? pick.points : derived.points

  // Game is pending if we don't have a score AND the pick hasn't been processed
  // (pick.score comes from fixture lookup which may fail even for processed picks)
  const isGamePending = !pick.score && !pick.processed && !pick.result

  return (
    <div className="w-full text-left">
      <div className={`flex items-start gap-3 py-3 px-3 -mx-3 rounded-xl
                        transition-colors
                        hover:bg-zinc-50 dark:hover:bg-zinc-800/50`}>
        {/* Matchday number */}
        <div className="flex flex-col items-center shrink-0 pt-0.5">
          <span className="text-[10px] font-medium uppercase tracking-wider text-zinc-400 dark:text-zinc-500">
            MD
          </span>
          <span className="text-lg font-bold tabular-nums text-zinc-900 dark:text-zinc-100 leading-none">
            {pick.matchday}
          </span>
        </div>

        {/* Main content */}
        <div className="flex-1 min-w-0">
          {/* Team and result row */}
          <div className="flex items-center gap-2 whitespace-nowrap overflow-hidden">
            <TeamLogo src={pick.teamLogo} name={pick.teamName} />
            <span className="text-sm font-semibold text-zinc-900 dark:text-zinc-100 truncate">
              {/* Show short name on mobile when comparing, full name otherwise */}
              {isComparing ? (
                <>
                  <span className="sm:hidden">{getShortName(pick.teamName)}</span>
                  <span className="hidden sm:inline">{getDisplayName(pick.teamName)}</span>
                </>
              ) : (
                getDisplayName(pick.teamName)
              )}
            </span>
            {/* DD badge inline with team name */}
            {pick.tddUsed && <InlineBadge label="DD" variant="tdd" />}
            {/* Show result badge and points if game has been played (we have a score) */}
            {!isGamePending && (
              <>
                <ResultBadge result={displayResult} />
                <PointsBadge points={displayPoints} />
              </>
            )}
          </div>

          {/* Match context row: H/A badge, vs Opponent, Score or Kickoff time */}
          {(pick.opponentName || pick.isHome !== null) && (
            <div className="flex items-center gap-1.5 mt-1 whitespace-nowrap overflow-hidden">
              {pick.isHome !== null && (
                <span className={`inline-flex items-center justify-center w-4 h-4 shrink-0 rounded text-[9px] font-bold
                                  ${pick.isHome
                                    ? 'bg-zinc-900 dark:bg-zinc-100 text-white dark:text-zinc-900'
                                    : 'bg-zinc-200 dark:bg-zinc-700 text-zinc-600 dark:text-zinc-400'}`}>
                  {pick.isHome ? 'H' : 'A'}
                </span>
              )}
              {pick.opponentName && (
                <span className="text-xs text-zinc-500 dark:text-zinc-400 truncate">
                  {/* Show short opponent name on mobile when comparing */}
                  {isComparing ? (
                    <>
                      <span className="sm:hidden">vs {getShortName(pick.opponentName)}</span>
                      <span className="hidden sm:inline">vs {pick.opponentName}</span>
                    </>
                  ) : (
                    <>vs {pick.opponentName}</>
                  )}
                </span>
              )}
              {/* Show score if game has been played */}
              {pick.score && (
                <span className="text-xs tabular-nums font-medium text-zinc-600 dark:text-zinc-300 shrink-0">
                  {pick.score.picked}&#8211;{pick.score.opponent}
                </span>
              )}
              {/* Show kickoff time only for truly pending games (no score yet) */}
              {isGamePending && pick.kickoffTime && (
                <span className="text-xs text-amber-600 dark:text-amber-400 shrink-0">
                  {formatKickoffTime(pick.kickoffTime)}
                </span>
              )}
              {hasGdBonus && (
                <span className="text-[10px] font-semibold text-emerald-500 shrink-0">
                  +{pick.gdBonus} GD
                </span>
              )}
            </div>
          )}

          {/* Badges row (red card, autopick, forfeit - DD is now in first row) */}
          {(hasRedCard || pick.isAutopick || pick.isForfeit) && (
            <div className="flex items-center gap-1.5 mt-1.5">
              {hasRedCard && <InlineBadge label="Red Card" variant="redcard" />}
              {pick.isAutopick && <InlineBadge label="Autopick" variant="autopick" />}
              {pick.isForfeit && <InlineBadge label="Forfeit" variant="forfeit" />}
            </div>
          )}
        </div>

        {/* Comparison column */}
        {isComparing && comparisonPick && (
          <div className="min-w-0 pl-2">
            <ComparisonColumn comparisonPick={comparisonPick} />
          </div>
        )}
      </div>
    </div>
  )
}
